// src/components/common/CountrySelect.tsx

import React, { useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';

interface CountrySelectProps {
    value: string;
    onChange: (value: string) => void;
    error?: string;
    disabled?: boolean;
    label?: string;
    required?: boolean;
    className?: string;
}

interface Country {
    code: string;
    name: string;
    flag: string;
    disabled?: boolean;
}

const COUNTRIES: Country[] = [
    { code: 'TT', name: 'Trinidad and Tobago', flag: '🇹🇹' },
    { code: 'other', name: 'Other countries (not yet supported)', flag: '🌍', disabled: true },
];

const CountrySelect: React.FC<CountrySelectProps> = ({
    value,
    onChange,
    error,
    disabled = false,
    label = 'Country',
    required = false,
    className = '',
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const selectedCountry = COUNTRIES.find(c => c.code === value) || COUNTRIES[0];

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className={className} ref={dropdownRef}>
            {label && (
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
            )}
            <div className="relative">
                <button
                    type="button"
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    disabled={disabled}
                    className={`w-full px-4 py-2.5 border rounded-lg shadow-sm text-sm transition-colors flex items-center justify-between ${
                        error
                            ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500'
                            : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                    } ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                >
                    <div className="flex items-center gap-2">
                        <span className="text-lg">{selectedCountry.flag}</span>
                        <span className="text-gray-700">{selectedCountry.name}</span>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                {isOpen && !disabled && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                        {COUNTRIES.map((country) => (
                            <button
                                key={country.code}
                                type="button"
                                onClick={() => {
                                    if (!country.disabled) {
                                        onChange(country.code);
                                        setIsOpen(false);
                                    }
                                }}
                                disabled={country.disabled}
                                className={`w-full px-4 py-2.5 text-sm flex items-center justify-between hover:bg-gray-50 transition-colors ${
                                    country.disabled
                                        ? 'opacity-50 cursor-not-allowed'
                                        : 'cursor-pointer'
                                } ${value === country.code ? 'bg-blue-50' : ''}`}
                            >
                                <div className="flex items-center gap-2">
                                    <span className="text-lg">{country.flag}</span>
                                    <span className={country.disabled ? 'text-gray-400' : 'text-gray-700'}>
                                        {country.name}
                                    </span>
                                </div>
                                {value === country.code && !country.disabled && (
                                    <Check className="h-4 w-4 text-blue-600" />
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            {error && (
                <p className="mt-1 text-sm text-red-600">{error}</p>
            )}
        </div>
    );
};

export default CountrySelect;